import { Bell, LogOut, User, Sun, Moon, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { SwitchUserMenu } from '@/components/auth/SwitchUserMenu';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';

export function Header() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isSwitchEnabled = import.meta.env.VITE_ENABLE_TEST_USER_SWITCH === 'true';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const userButton = (handleClick?: () => void, isSwitchOpen?: boolean) => (
    <button
      type="button"
      onClick={handleClick}
      aria-haspopup={isSwitchEnabled ? 'menu' : undefined}
      aria-expanded={isSwitchEnabled ? isSwitchOpen : undefined}
      className="flex items-center gap-2 rounded-lg p-1 hover:bg-surface-hover transition-colors cursor-pointer"
    >
      <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white text-sm font-medium">
        {user?.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
      </div>
      <span className="text-sm font-medium text-primary hidden sm:block">{user?.name}</span>
      {isSwitchEnabled && <ArrowLeftRight size={14} className="text-secondary flex-shrink-0 opacity-60" />}
    </button>
  );

  return (
    <header className="app-header sticky top-0 z-30 h-16 flex items-center justify-between px-6">
      <div className="flex items-center gap-4 ml-auto">
        <button
          onClick={toggleTheme}
          className="theme-toggle header-theme-toggle"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <Link
          to="/notifications"
          className="relative p-2 rounded-lg text-secondary hover:bg-surface-hover hover:text-primary transition-colors"
        >
          <Bell size={20} />
        </Link>

        <div ref={dropdownRef} className="relative">
          {isSwitchEnabled ? (
            <SwitchUserMenu side="bottom">
              {({ open }) => userButton(undefined, open)}
            </SwitchUserMenu>
          ) : (
            <>
              {userButton(() => setDropdownOpen(!dropdownOpen))}
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-default bg-surface shadow-lg z-50">
                  <div className="p-3 border-b border-default">
                    <p className="text-sm font-medium text-primary">{user?.name}</p>
                    <p className="text-xs text-secondary">{user?.email}</p>
                  </div>
                  <div className="p-1">
                    <Link
                      to="/profile"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-primary hover:bg-surface-hover"
                    >
                      <User size={16} />
                      Profile
                    </Link>
                    <button
                      onClick={() => { setDropdownOpen(false); logout(); }}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-error hover:bg-error/10 w-full"
                    >
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}
